import {
  Editor,
  getNodeList,
  Node,
  Plugin,
  StringNode,
  updateErrors,
  UseEditorOptions,
} from '@sagold/react-json-editor';

import {
  getCustomNode,
  getNodeFromJsonPath,
  isChangeEvent,
} from '../pluginHelper';

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseComparableUtcMillis(iso: string) {
  const trimmed = iso.trim();
  const m = ISO_DATE_ONLY.exec(trimmed);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || mo < 1 || mo > 12 || d < 1 || d > 31) {
      return null;
    }
    const utc = Date.UTC(y, mo - 1, d);
    const check = new Date(utc);
    if (
      check.getUTCFullYear() !== y ||
      check.getUTCMonth() !== mo - 1 ||
      check.getUTCDate() !== d
    ) {
      return null;
    }
    return utc;
  }
  const dt = new Date(trimmed);
  if (Number.isNaN(dt.getTime())) {
    return null;
  }
  return dt.getTime();
}

export function toIsoDateString(utcMillis: number) {
  const d = new Date(utcMillis);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

export const dateDraftConfig: UseEditorOptions['draftConfig'] = {
  validateFormat: {
    date: (node, value) => {
      if (typeof value !== 'string') {
        return;
      }

      const nodeMillis = parseComparableUtcMillis(value);
      if (nodeMillis === null) {
        return;
      }

      const { minDate, maxDate } = node.schema.options || {};

      const createDateError = (code: string, compareValue?: string) => ({
        type: 'error' as const,
        code,
        message: '',
        path: node.pointer,
        name: '',
        compareValue,
        data: {
          pointer: node.pointer,
          schema: node.schema,
          value,
        },
      });

      if (typeof minDate === 'string') {
        const minMillis = parseComparableUtcMillis(minDate);
        if (minMillis !== null && nodeMillis < minMillis) {
          return createDateError(
            'custom-min-date-error',
            toIsoDateString(minMillis)
          );
        }
      }

      if (typeof maxDate === 'string') {
        const maxMillis = parseComparableUtcMillis(maxDate);
        if (maxMillis !== null) {
          if (nodeMillis > maxMillis) {
            return createDateError(
              'custom-max-date-error',
              toIsoDateString(maxMillis)
            );
          }
          if (node.schema.options.shouldBeMax && nodeMillis < maxMillis) {
            return createDateError('custom-should-be-max-date-error');
          }
        }
      }

      return;
    },
  },
};

const setCustomDateErrors = (
  node: StringNode,
  rootNode: Node,
  draft: Editor['draft']
) => {
  if (!node.schema.options) {
    return;
  }
  const { minDateRef, maxDateRef } = node.schema.options;

  if (typeof minDateRef === 'string') {
    const minDateNode = getNodeFromJsonPath(
      node.pointer,
      minDateRef,
      rootNode
    ) as StringNode;
    if (minDateNode && minDateNode.value) {
      const minMillis = parseComparableUtcMillis(minDateNode.value);
      if (minMillis !== null) {
        node.schema.options.minDate = toIsoDateString(minMillis);
      }
    }
  }

  if (typeof maxDateRef === 'string') {
    const maxDateNode = getNodeFromJsonPath(
      node.pointer,
      maxDateRef,
      rootNode
    ) as StringNode;
    if (maxDateNode && maxDateNode.value) {
      const maxMillis = parseComparableUtcMillis(maxDateNode.value);
      if (maxMillis !== null) {
        node.schema.options.maxDate = toIsoDateString(maxMillis);
      }
    }
  }
  updateErrors(draft, node);
};

const setMaxOfDate = (node: StringNode, root: Node) => {
  const lookUpArrNode = getNodeFromJsonPath(
    node.pointer,
    node.schema.updateToMaxOf,
    root
  );
  if (lookUpArrNode == null) {
    return;
  }
  const arrayOfNodes = getNodeList(lookUpArrNode).filter(
    (n) => n.property === node.property
  ) as StringNode[];
  let maxMillis = 0;
  for (const n of arrayOfNodes) {
    if (n.value) {
      const millis = parseComparableUtcMillis(n.value);
      if (millis !== null) {
        maxMillis = Math.max(maxMillis, millis);
      }
    }
  }
  if (maxMillis > 0) {
    node.value = toIsoDateString(maxMillis);
  }
};

/**
 * This plugin directly modifies the error node value of react-json-editor, as rje does not have min max date validation by default
 */
export const DateValidationPlugin: Plugin = (he) => {
  return {
    id: 'custom-date-validation-plugin',
    onEvent(root, event) {
      if (!isChangeEvent(event)) {
        return undefined;
      }
      for (const node of getCustomNode(root, 'updateToMaxOf')) {
        if (node.type === 'string' && node.schema.format === 'date') {
          setMaxOfDate(node, root);
        }
      }
      for (const node of getNodeList(root)) {
        if (
          node.type === 'string' &&
          node.schema.format === 'date' &&
          node.options?.showErrorWithColor
        ) {
          setCustomDateErrors(node, root, he.draft);
        }
      }
      return [root, []];
    },
  };
};
